import React from "react";
import { useParams, Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import ConsultationCTASection from "../components/ConsultationCTASection";
import StatsStrip from "../components/StatsStrip";
import ServicesGrid from "../components/ServicesGrid";
import FreezonesGrid from "../components/FreezonesGrid";
import IndustriesGrid from "../components/IndustriesGrid";
import ProcessTimeline from "../components/ProcessTimeline";
import FAQSection from "../components/FAQSection";
import Testimonials from "../components/Testimonials";
import {
  Check,
  ArrowUpRight,
  Building2,
  Trees,
  Coins,
  Globe2,
  Users2,
  ShieldCheck,
  FileText,
  Scale,
} from "lucide-react";
import { useConsultation } from "../context/ConsultationContext";

const DATA = {
  mainland: {
    eyebrow: "Company Formation · Mainland",
    title: "Trade the entire UAE —",
    highlight: "no boundaries.",
    copy: "A Mainland LLC or sole establishment gives you direct access to the UAE market, public sector contracts and unrestricted trade anywhere in the Emirates.",
    image:
      "https://images.pexels.com/photos/9589001/pexels-photo-9589001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    includes: [
      "DED Trade License (commercial, professional, industrial)",
      "Memorandum of Association (MoA) drafting & notarisation",
      "Ejari office lease & approvals",
      "Initial approvals, tasheel & immigration file",
      "Corporate bank account introduction",
      "Annual compliance calendar & VAT registration",
    ],
    steps: [
      "Activity selection & trade name reservation",
      "Initial approval & legal structure (LLC / Sole Est. / Civil)",
      "Office lease (Ejari) & MoA notarisation",
      "Trade license issuance & establishment card",
      "Immigration file, visas, Emirates ID & medical",
      "Corporate bank account & tax registration",
    ],
    bestFor: ["Retail & F&B", "Construction & contracting", "Local professional services", "High-growth SMEs"],
    highlights: [
      { icon: Building2, t: "100% Ownership", d: "Full foreign ownership on most commercial activities." },
      { icon: Globe2, t: "Unlimited Market", d: "Trade anywhere in UAE and bid on government tenders." },
      { icon: Users2, t: "Unlimited Visas", d: "Visa quota scales with your Ejari office size." },
      { icon: ShieldCheck, t: "Strong Reputation", d: "Preferred by larger banks and institutional buyers." },
    ],
    structures: [
      { name: "LLC", d: "Most flexible for multi-activity trading and employment." },
      { name: "Sole Establishment", d: "Single owner for professional or consultancy work." },
      { name: "Civil Company", d: "Professional partnerships (law, medicine, engineering)." },
      { name: "Branch Office", d: "UAE arm of a foreign parent — same legal identity." },
    ],
  },
  freezone: {
    eyebrow: "Company Formation · Freezone",
    title: "0% tax. 100% ownership.",
    highlight: "Global by design.",
    copy: "Freezone structures are tailor-made for international founders — fast incorporation, full repatriation of capital and 0% corporate tax on qualifying income.",
    image:
      "https://images.pexels.com/photos/9589001/pexels-photo-9589001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    includes: [
      "Freezone license (IFZA, DMCC, Meydan, SHAMS, RAKEZ…)",
      "Flexi-desk, virtual or physical office",
      "Shareholder & UBO documentation",
      "Investor visa + dependents sponsorship",
      "Corporate bank account preparation",
      "ESR, UBO & VAT compliance setup",
    ],
    steps: [
      "Activity & freezone authority selection",
      "Name reservation & initial approval",
      "Shareholder KYC & incorporation documents",
      "License issuance & establishment card",
      "Investor visa, Emirates ID & medical",
      "Bank account opening & tax/ESR registration",
    ],
    bestFor: ["Tech startups & SaaS", "Consultants & freelancers", "E-commerce & trading", "Holding & IP companies"],
    highlights: [
      { icon: Coins, t: "0% Corporate Tax", d: "On qualifying income under the Qualifying Freezone Person regime." },
      { icon: Trees, t: "45+ Freezones", d: "Activity and cost-matched to your business model." },
      { icon: Globe2, t: "100% Repatriation", d: "Full repatriation of capital and profits — no restrictions." },
      { icon: Building2, t: "Flexible Office", d: "Flexi-desk, virtual office or full physical space." },
    ],
    structures: [
      { name: "FZCO / FZ-LLC", d: "Standard freezone entity with multiple shareholders." },
      { name: "FZE", d: "Single-shareholder freezone entity — fastest to incorporate." },
      { name: "Freelance Permit", d: "Solo operator permit — low-cost entry point." },
      { name: "Branch of Foreign Co.", d: "UAE freezone branch of a foreign parent." },
    ],
  },
};

export default function CompanyFormationPage() {
  const { type } = useParams();
  const { open } = useConsultation();
  const entry = DATA[type] || DATA.mainland;
  const otherType = type === "freezone" ? "mainland" : "freezone";

  return (
    <div data-testid={`formation-${type}-page`}>
      <PageHero
        eyebrow={entry.eyebrow}
        title={entry.title}
        highlight={entry.highlight}
        copy={entry.copy}
        image={entry.image}
      />

      <StatsStrip />

      {/* Highlights row */}
      <section className="bg-white py-16 md:py-20">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {entry.highlights.map(({ icon: Icon, t, d }, i) => (
            <div
              key={t}
              data-testid={`highlight-${i}`}
              className="p-6 rounded-sm border border-slate-200 bg-white hover:border-[#3366FF]/40 hover:shadow-md transition-all"
            >
              <div className="w-10 h-10 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </div>
              <div className="mt-5 font-heading font-semibold text-[#0F172A]">{t}</div>
              <div className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Includes + Best for */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline text-[#3366FF]">What's included</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-3xl md:text-4xl leading-tight text-[#0F172A]">
              A fully <span className="font-black text-[#002366]">turnkey</span> setup —
              no bolt-ons, no surprises.
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {entry.includes.map((t) => (
                <li key={t} className="flex gap-3 text-slate-700">
                  <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                  <span className="text-sm md:text-base">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <p className="overline text-[#3366FF] mb-4">Available structures</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {entry.structures.map((s) => (
                  <div
                    key={s.name}
                    className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-sm hover:border-[#3366FF]/40 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-[#3366FF] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-heading font-semibold text-[#0F172A] text-sm">
                        {s.name}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-sm border border-slate-200 p-8 bg-white shadow-sm">
              <p className="overline text-[#3366FF]">Best for</p>
              <ul className="mt-5 space-y-3">
                {entry.bestFor.map((b) => (
                  <li
                    key={b}
                    className="text-slate-800 flex items-center gap-3 py-1"
                  >
                    <Scale className="w-4 h-4 text-[#3366FF]" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-5 rounded-sm bg-[#002366] text-white">
                <div className="text-sm text-slate-300">Est. time to license</div>
                <div className="mt-1 font-heading font-black text-3xl">
                  {type === "mainland" ? "2–4 weeks" : "1–2 weeks"}
                </div>
                <div className="mt-3 text-sm text-slate-300">Est. year-one cost</div>
                <div className="mt-1 font-heading font-black text-3xl text-[#6B8AFF]">
                  {type === "mainland" ? "from AED 18,500*" : "from AED 12,500*"}
                </div>
                <p className="mt-2 text-[11px] text-slate-400">
                  *Indicative. Final figure depends on activity, visa count and office.
                </p>
              </div>
              <button
                data-testid="formation-cta"
                onClick={() => open(type || "other")}
                className="btn-gold mt-6 w-full"
              >
                Request a tailored quote <ArrowUpRight className="w-4 h-4" />
              </button>
              <Link
                to={`/company-formation/${otherType}`}
                className="mt-4 block text-center text-sm text-[#002366] underline-offset-4 hover:underline"
                data-testid="formation-switch"
              >
                Compare with {otherType === "mainland" ? "Mainland" : "Freezone"} →
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Process */}
      <ProcessTimeline />

      {/* Freezones list only for freezone page */}
      {type === "freezone" && <FreezonesGrid />}

      {/* Services */}
      <ServicesGrid />

      <IndustriesGrid />

      <Testimonials />

      <FAQSection />

      <ConsultationCTASection
        eyebrow={`Ready to start · ${type === "mainland" ? "Mainland" : "Freezone"}`}
        title={
          type === "mainland"
            ? "Let's scope your Mainland LLC this week."
            : "Let's choose the right Freezone for your growth."
        }
        service={type}
      />
    </div>
  );
}
